import { ViewRegistry, ModelRegistry, View } from '@lernetz/ts-lib';
import { data } from '@lernetz/ts-lib/src/Setup';


// html, show, svg, repeat:
import { HtmlView } from './utility/HtmlView';
import { Show as ShowView } from '@lernetz/ts-lib/src/components/Show';
import { Repeat as RepeatView } from '@lernetz/ts-lib/src/components/Repeat';
import { DebugMessage, DebugMessageView } from './utility/DebugMessage';

// Aside:
import {Aside, AsideView} from './aside/Aside';

// Audio:
import {Audio, AudioView} from './audio/Audio';

// Button:
import {Button, ButtonView} from './button/Button';

// Cartoon:
import {Cartoon, CartoonPanel, CartoonView} from './cartoon/Cartoon';

// CollapsibleSection:
import {CollapsibleSection, CollapsibleSectionView} from './collapsible-section/CollapsibleSection';

// CookieBanner:
import {CookieBanner, CookieBannerView} from './cookie-banner/CookieBanner';

// Footer:
import {FooterView} from './footer/Footer';

// Image:
import {Image, ImageView} from './image/Image';

// LinkedVideo:
import {LinkedVideo, LinkedVideoView} from './linked-video/LinkedVideo';

// ManualTwoColumnLayout:
import {ManualTwoColumnLayout, ManualTwoColumnLayoutView} from './manual-two-column-layout/ManualTwoColumnLayout';

// Materials (including Download, Link):
import {Download, Link, Materials, MaterialsView, NonMaterial} from './materials/Materials';

// NavBar:
import {NavBarView} from './nav-bar/NavBar';

// Page, TopicPage:
import {CoverImage, CoverImageView} from './page/CoverImage';
import {Page, PageView, TopicPage} from './page/Page';
import {TopicPageSection} from './page/TopicPageSection';

// PopupContainer:
import {PopupContainer, PopupContainerView} from './popup-container/PopupContainer';

// PopupPage:
import {PopupPage, PopupPageView} from './popup-page-buttons/PopupPage';

// PopupPageButton:
import {PopupPageButtons, PopupPageButtonsView} from './popup-page-buttons/PopupPageButtons';

// Quiz:
import {Answer, AnswerView} from './quiz/Answer';
import {Question, QuestionView} from './quiz/Question';
import {Quiz, QuizView} from './quiz/Quiz';
import {QuizLauncherButton, QuizLauncherButtonView} from './quiz/QuizLauncherButton';

// ScoreQuiz:
import { ScoreQuiz } from './quiz/ScoreQuiz';
import { ScoreQuizAnswer, ScoreAnswerView } from './quiz/ScoreQuizAnswer';
import { ScoreQuizEvaluation, ScoreQuizEvaluationView } from './quiz/ScoreQuizEvaluation';
import { ScoreQuizQuestion, ScoreQuestionView } from './quiz/ScoreQuizQuestion';
import { ScoreQuizResult } from './quiz/ScoreQuizResult';

// Text:
import {Heading, HeadingView} from './heading/Heading';
import {Paragraph, ParagraphView} from './paragraph/Paragraph';

// TopicsPreview:
import {TopicsPreview, TopicsPreviewView} from './topics-preview/TopicsPreview';
import {TopicView} from './topics-preview/Topic';
import { Asset } from './Asset';

const popupContainer = new PopupContainer();

export var modelRegistry = new ModelRegistry();
modelRegistry.add('App\\Aside', dto => new Aside(dto));
modelRegistry.add('App\\Asset', dto => new Asset(dto));
modelRegistry.add('App\\Audio', dto => new Audio(dto));
modelRegistry.add('App\\Answer', dto => new Answer( { correct: dto[ 'isCorrect' ] ?? false, ...dto } ) );
modelRegistry.add('App\\Button', dto => new Button(dto));
modelRegistry.add('App\\Cartoon', ( dto: any ) => {
    if( !dto.panels?.length ) {
        return new DebugMessage( 'Cartoon hat keine Panels erfasst!' );
    }
    const backgroundImage = dto[ 'panels' ][ 0 ].backgroundImage;
    const panels = dto[ 'panels' ].reduce( ( ps, p ) => {
        const areas = p[ 'areas' ].sort( ( a, b ) => a.order - b.order ).map( a => new CartoonPanel( a ) );
        return [ ...ps, ...areas ];
    }, [] );
    const width = dto[ 'width' ];
    const cartoon = new Cartoon({ id: dto[ 'id' ], alt: dto[ 'alt' ], backgroundImage, panels, poster: dto[ 'poster' ], width, marginTop: dto[ 'marginTop' ], marginBottom: dto[ 'marginBottom' ] });
    return cartoon;
});
modelRegistry.add('App\\CollapsibleSection', dto => new CollapsibleSection(dto));
modelRegistry.add('App\\CoverImage', dto => new CoverImage(dto));
modelRegistry.add('App\\Download', dto => new Download(dto));
modelRegistry.add('App\\Heading', dto => new Heading(dto));
modelRegistry.add('App\\Image', ( dto: any ) => {
    if( !dto.image?.file_name ) {
        return new DebugMessage( 'Bild nicht ausgewählt.' );
    }
    return new Image(dto);
});
modelRegistry.add('App\\Link', dto => new Link(dto));
modelRegistry.add('App\\LinkedVideo', dto => new LinkedVideo(dto));
modelRegistry.add('App\\ManualTwoColumnLayout', dto => new ManualTwoColumnLayout(dto));
modelRegistry.add('App\\Materials', dto => new Materials(dto));
modelRegistry.add('App\\NonMaterial', dto => new NonMaterial(dto));
modelRegistry.add('App\\Page', dto => new Page(dto));
modelRegistry.add('App\\Paragraph', dto => new Paragraph(dto));
modelRegistry.add('App\\PopupPage', dto => new PopupPage(dto));
modelRegistry.add('App\\PopupPageButtons', dto => new PopupPageButtons(dto, popupContainer));
modelRegistry.add('App\\Question', dto => new Question(dto));
modelRegistry.add('App\\Quiz', ( dto: any ) => {
    const quiz = new Quiz( {
        id: dto.id,
        openActionLabel: dto.openActionLabel,
        slides: [ ... dto.questions ],
        title: dto.title ?? '',
    } );
    return new QuizLauncherButton( quiz, popupContainer, { marginTop: dto.marginTop, marginBottom: dto.marginBottom } );
} );
modelRegistry.add('App\\ScoreQuiz', ( dto: any ) => {
    const evaluation = new ScoreQuizEvaluation();
    const quiz = new ScoreQuiz( {
        id: dto.id,
        openActionLabel: dto.openActionLabel,
        slides: [ ...dto.questions, evaluation ],
        results: dto.results,
        title: dto.title ?? '',
    } );
    evaluation.quiz = quiz;
    return new QuizLauncherButton( quiz, popupContainer, { marginTop: dto.marginTop, marginBottom: dto.marginBottom } );
} );
modelRegistry.add('App\\TopicPage', dto => new TopicPage(dto));
modelRegistry.add('App\\TopicPageSection', dto => new TopicPageSection(dto));
modelRegistry.add('App\\TopicsPreview', dto => new TopicsPreview(dto));

modelRegistry.add('App\\ScoreQuizResult', dto => new ScoreQuizResult(dto));
modelRegistry.add('App\\ScoreQuizQuestion', dto => new ScoreQuizQuestion(dto));
modelRegistry.add('App\\ScoreQuizAnswer', dto => new ScoreQuizAnswer(dto));

export var viewRegistry = new ViewRegistry();
viewRegistry.add(Aside, () => new AsideView());
viewRegistry.add(Audio, () => new AudioView());
viewRegistry.add(Button, () => new ButtonView());
viewRegistry.add(Cartoon, () => new CartoonView());
viewRegistry.add(CollapsibleSection, () => new CollapsibleSectionView());
viewRegistry.add(CoverImage, () => new CoverImageView());
viewRegistry.add(Heading, () => new HeadingView());
viewRegistry.add(Image, () => new ImageView());
viewRegistry.add(LinkedVideo, () => new LinkedVideoView());
viewRegistry.add(ManualTwoColumnLayout, () => new ManualTwoColumnLayoutView());
viewRegistry.add(Materials, () => new MaterialsView());
viewRegistry.add(Page, (data: Page) => new PageView(data));
viewRegistry.add(Paragraph, () => new ParagraphView());
viewRegistry.add(PopupPageButtons, () => new PopupPageButtonsView());
viewRegistry.add(Question, () => new QuestionView());
viewRegistry.add(Answer, () => new AnswerView());
viewRegistry.add(Quiz, () => new QuizView());
viewRegistry.add(QuizLauncherButton, () => new QuizLauncherButtonView());
viewRegistry.add(ScoreQuiz, () => new QuizView());
viewRegistry.add(ScoreQuizEvaluation, () => new ScoreQuizEvaluationView());
viewRegistry.add(ScoreQuizQuestion, () => new ScoreQuestionView());
viewRegistry.add(ScoreQuizAnswer, () => new ScoreAnswerView());
viewRegistry.add(PopupPage, () => new PopupPageView());
viewRegistry.add(TopicPage, (data: TopicPage) => new PageView(data));
viewRegistry.add(TopicsPreview, () => {
    const view = new TopicsPreviewView();
    view.registry.add('Topic', () => new TopicView());
    return view;
});

viewRegistry.add('CookieBanner', () => new CookieBannerView());
viewRegistry.add('Footer', () => new FooterView(data('footerLinks')));
viewRegistry.add('PopupContainer', () => new PopupContainerView({ data: popupContainer }));
viewRegistry.add('NavBar', () => new NavBarView(data('page').title, data('locales')));
viewRegistry.add('html', () => new HtmlView());
viewRegistry.add('show', () => new ShowView());
viewRegistry.add('repeat', () => new RepeatView());
viewRegistry.add('empty', () => new View({ template: '<div></div>' }));
viewRegistry.add(DebugMessage, () => new DebugMessageView());
